/* ==========================================================================
   config.js — single source of truth for all markets
   --------------------------------------------------------------------------
   This object drives EVERYTHING on the page: copy, Amazon CTA, map search,
   and the full "where to buy" directory. To add/edit a market or retailer,
   change it here only. Designed to lift cleanly into a Shopify section later.

   The map stack is keyless (MapLibre + OpenStreetMap/Overpass + OSRM); the
   only thing you must set before going live is SETTINGS.metaPixelId.
   ========================================================================== */

const SETTINGS = {
  // Meta Pixel ID — leave as null to disable tracking while developing.
  metaPixelId: '1008797365885977',
  // Used only when nothing else identifies the visitor's market (see market.js).
  defaultMarket: 'us',

  // — Map providers (all keyless; swappable for paid tiers if needed) —
  // Vector basemap style for MapLibre GL.
  mapStyle: 'https://tiles.openfreemap.org/styles/positron',
  // Live store data: OpenStreetMap via Overpass. Mirrors tried in order.
  // For production reliability, point the first entry at a dedicated/hosted
  // Overpass endpoint (the public demo servers rate-limit under load).
  overpassEndpoints: [
    'https://overpass-api.de/api/interpreter',
    'https://overpass.openstreetmap.fr/api/interpreter',
    'https://maps.mail.ru/osm/tools/overpass/api/interpreter',
    'https://overpass.kumi.systems/api/interpreter',
  ],
  // Per-endpoint client-side timeout (ms). The query's own [timeout:25] is
  // SERVER-side only and won't fire if a mirror hangs at the network level, so
  // we abort here and fall through to the next mirror fast instead of stalling.
  overpassTimeoutMs: 8000,
  // Driving distance + ETA for the closest stores (OSRM public demo).
  osrmEndpoint: 'https://router.project-osrm.org',

  // Search behaviour
  searchRadiusMeters: 30000, // ~19 miles / 30 km
  closestCount: 5,           // featured (blue, listed first) nearest stores
  listMax: 25,               // max stores available in the list (load-more)
  maxMapPins: 60,            // cap total pins (closest + grey) to avoid clutter
};

/* Helper: safe fallback link for retailers without a confirmed deep link. */
const findOnline = (name, market) =>
  `https://www.google.com/search?q=${encodeURIComponent('Naty ' + name + ' ' + market)}`;

const MARKETS = {
  /* ===================== UNITED STATES ===================== */
  us: {
    code: 'us',
    locale: 'en-US',
    label: 'United States',
    flag: '🇺🇸',
    country: 'US',       // ISO 3166-1: geocoding region + store-data boundary
    distanceUnit: 'mi',
    amazonUrl:
      'https://www.amazon.com/stores/Eco+by+Naty/page/E836F6FB-9CE7-4098-9F9C-A9D905AF78D8?ref_=ast_bln',
    mapCenter: { lat: 39.5, lng: -98.35 },
    mapZoom: 4,
    strings: {
      eyebrow: 'Plant-based care',
      heroTitle: 'Find Naty\nnear you',
      heroLead:
        'Naty diapers and period care, made from plants — now on shelves and online across the U.S.',
      amazonCta: 'Buy on Amazon',
      browseCta: 'See all stores',
      finderTitle: 'Stores near you',
      finderLead: 'Share your location or enter a ZIP code to find Naty stockists nearby.',
      locationPlaceholder: 'Enter ZIP code',
      useLocation: 'Use my location',
      searchBtn: 'Search',
      directoryTitle: 'Where to buy in the U.S.',
      inStore: 'In store',
      online: 'Online',
      directions: 'Directions',
      emptyTitle: 'No nearby stockists found',
      emptyLead: 'You can always get Naty delivered fast on Amazon.',
      disclaimer: 'Store list is indicative. Product availability and stock of Naty may vary by location.',
      closestTitle: 'Your 5 closest stores',
      driveLabel: 'min drive',
      walkLabel: 'min walk',
      loadMore: 'Show more stores',
      mapPlaceholder: 'Search a location to see Naty stockists on the map.',
      searching: 'Finding Naty near you…',
      errorSearch: 'Something went wrong loading the map. Please try again.',
      errorLocation: 'We couldn’t find that location. Check the ZIP code and try again.',
      errorGeo: 'Location access was blocked. Enter a ZIP code instead.',
      trust2: 'Plant-based materials',
      trust3: 'Dermatologically tested',
      amazonTag: 'Fastest delivery',
      footerNote: 'Naty — plant-based diapers & period care. Retailer names and logos are the property of their respective owners and are shown to indicate product availability.',
    },
    physicalChains: [
      { name: 'Walmart',   osm: 'Walmart',   url: 'https://www.walmart.com/search/?query=eco%20by%20naty' },
      { name: 'Target',    osm: 'Target',    url: 'https://www.target.com/p/eco-by-naty-premium-disposable-diapers-for-sensitive-skin-select-size-and-count/-/A-79144480?preselect=54575078' },
      { name: 'Walgreens', osm: 'Walgreens', url: 'https://www.walgreens.com/search/results.jsp?Ntt=Eco%20by%20Naty' },
      { name: 'Kroger',    osm: 'Kroger',    url: 'https://www.kroger.com/search?query=naty&searchType=default_search&fulfillment=all' },
    ],
    onlineRetailers: [
      { name: 'iHerb',          url: 'https://www.iherb.com/c/naty' },
      { name: 'Vitacost',       url: 'https://www.vitacost.com/productsearch.aspx?t=eco%20by%20naty' },
      { name: 'EarthHero',      url: 'https://earthhero.com/product-category/baby-kids/baby/diapering?fwp_brand=eco-by-naty' },
      { name: 'Staples',        url: 'https://www.staples.com/eco+by+naty/directory_eco%2520by%2520naty' },
      { name: 'Greenline',      url: 'https://thegreenlinemarket.com/brands/naty/' },
      { name: 'Do Good Diapers', url: 'https://dogooddiapers.com/product-category/diapering/' },
      { name: 'Red Bottle',     url: 'http://rbottle.com/Search/Product/result/keyword/Naty' },
    ],
  },

  /* ===================== UNITED KINGDOM ===================== */
  uk: {
    code: 'uk',
    locale: 'en-GB',
    label: 'United Kingdom',
    flag: '🇬🇧',
    country: 'GB',
    distanceUnit: 'mi',
    amazonUrl:
      'https://www.amazon.co.uk/stores/Naty/page/A210718B-1A3E-433D-9C1B-C9B74E5FD307?lp_asin=B0F8BCTG5Z&store_ref=bl_ast_dp_brandlogo_sto&bl_grd_status=override',
    mapCenter: { lat: 54.0, lng: -2.5 },
    mapZoom: 5,
    strings: {
      eyebrow: 'Plant-based care',
      heroTitle: 'Find Naty\nnear you',
      heroLead:
        'Naty nappies and period care — award-winning, certified care that performs. Trusted since 1994. Stocked in supermarkets and online across the UK.',
      amazonCta: 'Buy on Amazon',
      browseCta: 'See all stores',
      finderTitle: 'Stores near you',
      finderLead: 'Share your location or enter a postcode to find Naty stockists nearby.',
      locationPlaceholder: 'Enter postcode',
      useLocation: 'Use my location',
      searchBtn: 'Search',
      directoryTitle: 'Where to buy in the UK',
      inStore: 'In store & online',
      online: 'Online',
      directions: 'Directions',
      emptyTitle: 'No nearby stockists found',
      emptyLead: 'You can always get Naty delivered fast on Amazon.',
      disclaimer: 'Store list is indicative. Product availability and stock of Naty may vary by location.',
      closestTitle: 'Your 5 closest stores',
      driveLabel: 'min drive',
      walkLabel: 'min walk',
      loadMore: 'Show more stores',
      mapPlaceholder: 'Search a location to see Naty stockists on the map.',
      searching: 'Finding Naty near you…',
      errorSearch: 'Something went wrong loading the map. Please try again.',
      errorLocation: 'We couldn’t find that location. Check the postcode and try again.',
      errorGeo: 'Location access was blocked. Enter a postcode instead.',
      trust2: 'Plant-based materials',
      trust3: 'Dermatologically tested',
      amazonTag: 'Fastest delivery',
      footerNote: 'Naty — plant-based nappies & period care. Retailer names and logos are the property of their respective owners and are shown to indicate product availability.',
      certsEyebrow: 'Certified, not just claimed',
      certsTitle: 'Safety you can verify',
      certsLead: 'Every Naty product is independently tested and third-party certified — proof-first, never just a marketing claim.',
      certsReadMore: 'Read more',
    },
    physicalChains: [
      { name: 'Waitrose',           osm: 'Waitrose',    url: 'https://www.waitrose.com/ecom/shop/search?&searchTerm=Naty' },
      { name: 'Boots',              osm: 'Boots',       url: 'https://www.boots.com/sitesearch?searchTerm=Naty' },
      { name: 'Booths',             osm: 'Booths',      url: findOnline('Booths', 'UK') },
    ],
    onlineRetailers: [
      { name: 'Ocado',              url: 'https://www.ocado.com/search?entry=Naty' },
      { name: 'Big Green Smile',    url: 'https://www.biggreensmile.com/search.aspx?q=Naty' },
      { name: 'Ethical Superstore', url: 'https://www.ethicalsuperstore.com/products/nature-babycare/' },
      { name: 'Natural Collection', url: 'https://www.naturalcollection.com/shop/nature-babycare/' },
      { name: 'PlantX',             url: 'https://plantx.uk/collections/eco-by-naty' },
      { name: 'Half Moon Wholefoods', url: 'https://www.halfmoonwholefoods.co.uk/index.php?main_page=advanced_search_result&search_in_description=1&keyword=Naty' },
    ],
  },

  /* ===================== GERMANY ===================== */
  de: {
    code: 'de',
    locale: 'de-DE',
    label: 'Deutschland',
    flag: '🇩🇪',
    country: 'DE',
    distanceUnit: 'km',
    amazonUrl:
      'https://www.amazon.de/stores/Eco+by+Naty/page/3EC56757-5489-49F3-9D49-1CDA5107DE0C?ref_=ast_bln',
    mapCenter: { lat: 51.2, lng: 10.4 },
    mapZoom: 5,
    strings: {
      eyebrow: 'Pflanzenbasierte Pflege',
      heroTitle: 'Naty in\ndeiner Nähe',
      heroLead:
        'Naty Windeln und Periodenpflege auf pflanzlicher Basis — im Handel und online in ganz Deutschland.',
      amazonCta: 'Bei Amazon kaufen',
      browseCta: 'Alle Märkte ansehen',
      finderTitle: 'Märkte in deiner Nähe',
      finderLead: 'Teile deinen Standort oder gib eine PLZ ein, um Naty-Händler in der Nähe zu finden.',
      locationPlaceholder: 'PLZ eingeben',
      useLocation: 'Standort verwenden',
      searchBtn: 'Suchen',
      directoryTitle: 'Wo du Naty in Deutschland kaufst',
      inStore: 'Im Markt',
      online: 'Online',
      directions: 'Route',
      emptyTitle: 'Keine Händler in der Nähe gefunden',
      emptyLead: 'Naty gibt es jederzeit schnell geliefert bei Amazon.',
      disclaimer: 'Die Liste ist ein Richtwert. Verfügbarkeit und Bestand von Naty können je nach Markt variieren.',
      closestTitle: 'Deine 5 nächsten Märkte',
      driveLabel: 'Min. Fahrt',
      walkLabel: 'Min. zu Fuß',
      loadMore: 'Mehr Märkte anzeigen',
      mapPlaceholder: 'Gib einen Standort ein, um Naty-Händler auf der Karte zu sehen.',
      searching: 'Naty in deiner Nähe wird gesucht…',
      errorSearch: 'Beim Laden der Karte ist etwas schiefgelaufen. Bitte versuche es erneut.',
      errorLocation: 'Diesen Ort konnten wir nicht finden. Prüfe die PLZ und versuche es erneut.',
      errorGeo: 'Der Standortzugriff wurde blockiert. Gib stattdessen eine PLZ ein.',
      trust2: 'Pflanzliche Materialien',
      trust3: 'Dermatologisch getestet',
      amazonTag: 'Schnellste Lieferung',
      footerNote: 'Naty — pflanzenbasierte Windeln & Periodenpflege. Marken- und Händlernamen sowie Logos sind Eigentum der jeweiligen Inhaber und dienen nur zur Angabe der Verfügbarkeit.',
    },
    physicalChains: [
      { name: 'dm',              osm: 'dm',          brand: '^dm($|[ -])',               url: 'https://www.dm.de/search?query=Eco%20by%20Naty&searchType=product' },
      { name: 'Müller',          osm: 'Müller',      brand: '^(Drogeriemarkt )?Müller$', url: 'https://www.mueller.de/search/?q=naty&ms=true' },
      { name: "denn's Biomarkt", osm: "denn's",      brand: '^denn.?s',                  url: findOnline("denn's Biomarkt", 'DE') },
      { name: 'Alnatura',        osm: 'Alnatura',    brand: '^Alnatura',                 url: findOnline('Alnatura', 'DE') },
      { name: 'tegut',           osm: 'tegut',       brand: '^tegut',                    url: findOnline('tegut', 'DE') },
      { name: 'Globus',          osm: 'Globus',      brand: '^Globus$',                  url: findOnline('Globus', 'DE') },
    ],
    onlineRetailers: [
      { name: 'windelnkaufen.de', url: 'https://www.windelnkaufen.de/search?search=naty' },
      { name: 'BIOAUFVORRAT',     url: 'https://bioaufvorrat.de/bio-produkte/marken/eco-by-naty/' },
      { name: 'Little Greenie',   url: 'https://www.littlegreenie.de/eco-by-naty/' },
      { name: 'Terra Naturkost',  url: 'https://shop.terra-natur.com/' },
      { name: 'Limango',          url: 'https://www.limango.de/shop/products?q=naty' },
      { name: 'Babykorb',         url: 'https://www.babykorb.de/search?search=eco+by+naty' },
      { name: 'Kitaeinkauf',      url: 'https://www.kitaeinkauf.de/search?query=Naty' },
      { name: 'No Waste Wrapping', url: 'https://www.nowastewrapping.de/index.php?qs=Naty&search=' },
      { name: 'Biogarten',        url: 'https://kundenportal.biogarten.de/login' },
    ],
  },
};

/* Regex source matching a chain's OSM `brand` tag (case-insensitive). `osm` is
   a plain substring; set `brand` instead where that is too loose or too strict
   — OSM tags dm as "dm" and denn's as "Denns BioMarkt", and a bare "Müller" or
   "Globus" also catches bike brands, bakeries and DIY stores. Shared by map.js
   (live fallback) and scripts/build-stockists-stores.mjs. */
const brandPattern = (chain) =>
  chain.brand || chain.osm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/* Retailer logos — Naty's own curated brand library (downloaded to
   assets/img/logos/). Keyed by the exact retailer `name` used above.
   Missing key → the card falls back to a styled text name. */
const LOGOS = {
  // US
  Walmart: 'walmart.jpg', Target: 'target.jpg', Walgreens: 'walgreens.jpg', Kroger: 'kroger-logo.png',
  iHerb: 'iherb.jpg', Vitacost: 'vitacost-logo.png', EarthHero: 'earthhero.jpg', Staples: 'staples-logo.png',
  Greenline: 'greenline-logo.jpg', 'Do Good Diapers': 'dogooddiapers.jpg', 'Red Bottle': 'rbottle.jpg',
  // UK
  "Sainsbury's": 'sainsburys-logo.jpg', Waitrose: 'waitrose-logo.jpg', Boots: 'boots-logo.jpg',
  'Whole Foods Market': 'whole-foods-logo.jpg', Booths: 'booths-logo.jpg', Ocado: 'ocado-logo.jpg',
  'Big Green Smile': 'big-green-smile-logo.jpg', 'Ethical Superstore': 'ethical-superstore-logo.jpg',
  'Natural Collection': 'natural-collections-logo.jpg', 'Half Moon Wholefoods': 'half-moon-wholefoods-logo.jpg',
  // DE
  dm: 'dm.jpg', 'Müller': 'muller.jpg', "denn's Biomarkt": 'denns.jpg', Alnatura: 'alnatura.jpg',
  tegut: 'tegut.jpg', Globus: 'globus.jpg', 'windelnkaufen.de': 'windelnkaufen.jpg',
  BIOAUFVORRAT: 'bioaufvorrat-logo.jpg', 'Little Greenie': 'littlegreenie.jpg', 'Terra Naturkost': 'terra.jpg',
  Limango: 'limango.jpg', Babykorb: 'babykorb.png', Kitaeinkauf: 'kitaeinkauf-logo.jpg',
  'No Waste Wrapping': 'no-waste-wrapping-logo.jpg', Biogarten: 'biogarten.jpg',
  // Amazon (used on the light variant only)
  Amazon: 'amazon-logo.jpg',
};

window.NATY = { SETTINGS, MARKETS, LOGOS, brandPattern };
